import { Stack, StackProps, styled, Typography, TypographyProps } from '@mui/material';

import AmazonImg from '@/shared/assets/images/partners/amazon.png';
import FameplayImg from '@/shared/assets/images/partners/fameplay.png';
import MengmiuImg from '@/shared/assets/images/partners/mengmiu.png';
import PublicisImg from '@/shared/assets/images/partners/publicis.png';
import SemrushImg from '@/shared/assets/images/partners/semrush.png';

type ContainerProps = {
  backgroundColor?: string;
  isSmallFromTablet: boolean;
} & StackProps;

export const Container = styled(({ backgroundColor, isSmallFromTablet, ...props }: ContainerProps) => (
  <Stack {...props} />
))(({ theme, isSmallFromTablet, backgroundColor }) => ({
  justifyContent: 'center',
  alignItems: 'center',
  background: backgroundColor ?? (isSmallFromTablet ? theme.palette.primary[25] : theme.palette.grey[50]),
  borderRadius: 24,
  padding: '48px 24px',
  gap: 32,

  ...(isSmallFromTablet && {
    [theme.breakpoints.up('tablet')]: {
      alignItems: 'flex-start',
      gap: 12,
      padding: 0,
    },
  }),

  [theme.breakpoints.up('laptop')]: {
    ...(!isSmallFromTablet && { gap: 12 }),
  },

  [theme.breakpoints.up('desktop')]: {
    padding: isSmallFromTablet ? 0 : 64,
  },
}));

type PartnersTitleProps = {
  isSmallFromTablet: boolean;
} & TypographyProps;

export const PartnersTitle = styled(({ isSmallFromTablet, ...props }: PartnersTitleProps) => <Typography {...props} />)(
  ({ theme, isSmallFromTablet }) => ({
    ...theme.typography['body-1-md'],
    color: theme.palette.grey[500],

    ...(isSmallFromTablet && {
      [theme.breakpoints.up('tablet')]: {
        ...theme.typography['caption-1'],
      },
    }),

    [theme.breakpoints.up('laptop')]: {
      ...(isSmallFromTablet
        ? theme.typography['caption-1']
        : {
            ...theme.typography['title-3-md'],
            color: theme.palette.common.black,
            fontSize: 20,
            fontWeight: 400,
            letterSpacing: '-0.4px',
            lineHeight: '20px',
          }),
    },
  }),
);

type PartnersIconsContainerProps = {
  isSmallFromTablet: boolean;
} & StackProps;

export const PartnersIconsContainer = styled(({ isSmallFromTablet, ...props }: PartnersIconsContainerProps) => (
  <Stack {...props} />
))(({ theme, isSmallFromTablet }) => ({
  gap: 28,
  alignItems: 'center',
  flexDirection: 'column',

  '& > img': {
    transform: 'scale(0.7)',
  },

  ...(isSmallFromTablet && {
    [theme.breakpoints.up('tablet')]: {
      ...theme.typography['caption-1'],
      '& > img': {
        transform: 'scale(1)',
      },
      flexDirection: 'row',

      gap: 14.5,
    },
  }),

  [theme.breakpoints.up('laptop')]: {
    '& > img': {
      transform: 'scale(1)',
    },
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: isSmallFromTablet ? 14.5 : 16,
    justifyContent: 'space-between',

    width: isSmallFromTablet ? 'auto' : '100%',
  },
}));

type IconProps = {
  isSmallFromTablet: boolean;
};

export const FameplayIcon = styled(({ isSmallFromTablet, ...props }: IconProps) => (
  <img alt="Fameplay" src={FameplayImg} {...props} />
))(({ theme, isSmallFromTablet }) => ({
  width: 127,
  height: 48,

  ...(isSmallFromTablet && {
    [theme.breakpoints.up('tablet')]: {
      height: 17,
      width: 44,
    },
  }),
}));

export const AmazonIcon = styled(({ isSmallFromTablet, ...props }: IconProps) => (
  <img alt="Amazon" src={AmazonImg} {...props} />
))(({ theme, isSmallFromTablet }) => ({
  width: 132,
  height: 40,

  ...(isSmallFromTablet && {
    [theme.breakpoints.up('tablet')]: {
      height: 14,
      width: 46,
    },
  }),
}));

export const SemrushIcon = styled(({ isSmallFromTablet, ...props }: IconProps) => (
  <img alt="Semrush" src={SemrushImg} {...props} />
))(({ theme, isSmallFromTablet }) => ({
  width: 269,
  height: 36,

  ...(isSmallFromTablet && {
    [theme.breakpoints.up('tablet')]: {
      height: 12,
      width: 94,
    },
  }),
}));

export const MengmiuIcon = styled(({ isSmallFromTablet, ...props }: IconProps) => (
  <img alt="Mengmiu" src={MengmiuImg} {...props} />
))(({ theme, isSmallFromTablet }) => ({
  width: 135,
  height: 40,

  ...(isSmallFromTablet && {
    [theme.breakpoints.up('tablet')]: {
      height: 14,
      width: 47,
    },
  }),
}));

export const PublicisIcon = styled(({ isSmallFromTablet, ...props }: IconProps) => (
  <img alt="Publicis" src={PublicisImg} {...props} />
))(({ theme, isSmallFromTablet }) => ({
  width: 177,
  height: 40,

  ...(isSmallFromTablet && {
    [theme.breakpoints.up('tablet')]: {
      height: 14,
      width: 62,
    },
  }),
}));
